<template>
  <div class="bg-white rounded-xl border border-gray-100 p-5 shadow-sm hover:shadow-md transition">
    <div class="flex flex-col gap-4 md:flex-row md:items-center">
      <div class="flex-1 min-w-0">
        <div class="flex items-start justify-between gap-3">
          <RouterLink :to="`/posts/${post.id}`" class="min-w-0">
            <h3 class="text-lg font-semibold text-gray-700 line-clamp-2 hover:text-blue-600 transition-colors">
              {{ post.title }}
            </h3>
          </RouterLink>
          <span
            class="inline-flex px-2.5 py-1 text-[11px] font-bold rounded-full uppercase tracking-wide whitespace-nowrap"
            :class="{
              'bg-green-200 text-green-800': status === 'available',
              'bg-red-200 text-red-800': status === 'full',
              'bg-blue-200 text-blue-800': status === 'upcoming',
              'bg-gray-200 text-gray-500': status === 'closed'
            }"
          >
            {{ statusText }}
          </span>
        </div>

        <div class="mt-1 flex items-center gap-2 text-sm text-gray-500 min-w-0">
          <MapPinIcon class="w-4 h-4 text-gray-400 flex-shrink-0" />
          <span class="truncate">{{ post.place }}</span>
        </div>

        <p v-if="post.content" class="mt-2 text-sm text-gray-600 line-clamp-2">
          {{ post.content }}
        </p>

        <div class="mt-3 grid gap-2 text-sm text-gray-600 sm:grid-cols-2">
          <div class="flex items-start gap-2 min-w-0">
            <CalendarDaysIcon class="w-4 h-4 text-blue-400 flex-shrink-0 mt-0.5" />
            <div class="min-w-0 leading-5">
              <div class="truncate" :title="formatDateTime(post.startDate)">
                시작 {{ formatDateTime(post.startDate) }}
              </div>
              <div class="truncate text-gray-500" :title="formatDateTime(post.endDate)">
                종료 {{ formatDateTime(post.endDate) }}
              </div>
            </div>
          </div>
          <div class="flex items-center gap-2 min-w-0">
            <UserGroupIcon class="w-4 h-4 text-blue-400 flex-shrink-0" />
            <span
              class="truncate"
              :class="{
                'text-green-600 font-medium': status === 'available',
                'text-red-600 font-medium': status === 'full',
                'text-blue-600 font-medium': status === 'upcoming',
                'text-gray-400 font-medium': status === 'closed'
              }"
            >
              {{ availabilityText }}
            </span>
          </div>
        </div>
      </div>

      <div class="flex items-center justify-between gap-4 md:flex-col md:items-end md:justify-center">
        <button
          type="button"
          class="hidden sm:flex items-center justify-center rounded-lg border border-gray-200 bg-gray-50 p-2 hover:border-blue-200 hover:bg-blue-50 transition"
          @click="showSeatPreview = true"
        >
          <SeatLayoutMini :layout="seatLayoutInfo" :reserved-seat-numbers="reservedSeatNumbers" />
        </button>
        <button
          type="button"
          class="sm:hidden text-xs text-gray-500 hover:text-blue-600"
          @click="showSeatPreview = true"
        >
          좌석 보기
        </button>

        <div class="flex items-center gap-2">
          <RouterLink :to="`/posts/${post.id}`" class="btn btn-outline btn-sm text-gray-500">
            자세히
          </RouterLink>
          <button
            v-if="status === 'available'"
            @click="$router.push(`/posts/${post.id}`)"
            class="btn btn-primary btn-sm bg-blue-600 hover:bg-blue-700 text-white flex items-center"
          >
            <ClockIcon class="w-4 h-4 mr-1" />
            예약하기
          </button>
          <span v-else-if="status === 'upcoming'" class="text-sm text-blue-600">예약 전</span>
          <span v-else-if="status === 'closed'" class="text-sm text-gray-500">예약 마감</span>
          <span v-else class="text-sm text-red-600">만석</span>
        </div>
      </div>
    </div>

    <SeatPreviewModal
      :show="showSeatPreview"
      :title="post.title"
      :layout="seatLayoutInfo"
      :reserved-seat-numbers="reservedSeatNumbers"
      @close="showSeatPreview = false"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import { RouterLink } from 'vue-router'
import {
  MapPinIcon,
  CalendarDaysIcon,
  UserGroupIcon,
  ClockIcon
} from '@heroicons/vue/24/outline'
import type { Post, SeatLayoutInfo } from '@/types'
import SeatLayoutMini from '@/components/SeatLayoutMini.vue'
import SeatPreviewModal from '@/components/SeatPreviewModal.vue'

interface Props {
  post: Post
  seatLayoutInfo: SeatLayoutInfo
  reservedSeatNumbers?: number[] | undefined
}

const props = withDefaults(defineProps<Props>(), {
  reservedSeatNumbers: undefined
})

const showSeatPreview = ref(false)

const totalSeatCount = computed(() => {
  const postTotal = props.post.totalSeats ?? 0
  if (postTotal > 0) return postTotal
  return props.seatLayoutInfo?.totalSeats ?? 0
})

const reservedSeatCount = computed(() => {
  if (props.reservedSeatNumbers !== undefined) {
    return props.reservedSeatNumbers.length
  }
  return props.post.reservedSeats ?? 0
})

const status = computed(() => {
  const now = new Date()
  const startDate = new Date(props.post.startDate)
  const endDate = new Date(props.post.endDate)
  const hasAvailableSeats = totalSeatCount.value > reservedSeatCount.value

  if (now < startDate) return 'upcoming'
  if (now > endDate) return 'closed'
  return hasAvailableSeats ? 'available' : 'full'
})

const statusText = computed(() => {
  switch (status.value) {
    case 'upcoming':
      return '예약전'
    case 'closed':
      return '예약마감'
    case 'full':
      return '만석'
    default:
      return '예약가능'
  }
})

const availabilityText = computed(() => {
  const availableSeats = totalSeatCount.value - reservedSeatCount.value

  if (status.value === 'upcoming') return '예약 시작 전'
  if (status.value === 'closed') return `${reservedSeatCount.value} / ${totalSeatCount.value} 예약완료`
  if (status.value === 'full') return '만석'
  return `${availableSeats}석 남음 (전체 ${totalSeatCount.value}석)`
})

const formatDateTime = (dateString: string) => {
  const date = new Date(dateString)
  const datePart = `${date.getFullYear().toString().slice(2)}.${String(
    date.getMonth() + 1
  ).padStart(2, '0')}.${String(date.getDate()).padStart(2, '0')}`
  const timePart = `${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(
    2,
    '0'
  )}`
  return `${datePart} ${timePart}`
}
</script>
